import {
  AltRedirectCampaigns,
  DateFormatType,
} from "../services/altRedirect/types";
import { captureSentryException } from "shared/services/sentry/captureException";
import { getCookie, setCookie } from "./cookies";
import formatMultiTypeDate from "./formatMultiTypeDate";

/**
 * Retorna a campanha correspondente com base nos critérios.
 */
const getAltRedirectCampaign = (
  originAirport: string,
  destinationAirport: string,
  isMobile: boolean,
  altRedirectCampaign: AltRedirectCampaigns | null,
) => {
  if (!altRedirectCampaign?.CAMPAIGNS?.length) return null;

  const campaign = altRedirectCampaign.CAMPAIGNS.find((c) => {
    const originAirports = c.ORIGIN_AIRPORTS.split(",").map((a) => a.trim());
    const destinationAirports = c.DESTINATION_AIRPORTS.split(",").map((a) =>
      a.trim(),
    );

    return (
      c.ACTIVE &&
      (isMobile ? c.TYPE === "mobile" : c.TYPE === "desktop") &&
      (originAirports.includes(originAirport) ||
        originAirports.includes("ALL")) &&
      (destinationAirports.includes(destinationAirport) ||
        destinationAirports.includes("ALL"))
    );
  });

  if (!campaign) return null;

  const originAirports = campaign.ORIGIN_AIRPORTS.split(",").map((a) =>
    a.trim(),
  );
  const destinationAirports = campaign.DESTINATION_AIRPORTS.split(",").map(
    (a) => a.trim(),
  );

  return {
    ...campaign,
    COOKIE_NAME: `${campaign.NAME}-${originAirports.join(",")}-${destinationAirports.join(",")}-${
      isMobile ? "mobile" : "desktop"
    }`,
  };
};

type AltRedirectParams = {
  originAirport: string;
  destinationAirport: string;
  departureDates: string[];
  returnDates?: string[];
  adults: number;
  children: number;
  babies: number;
  dateFormat: DateFormatType;
};

const resolveAltRedirectUrl = (
  template: string,
  params: AltRedirectParams,
): string => {
  let url = template;

  const getDepartureDate = (index: number) =>
    params.departureDates[index] ?? params.departureDates[0];

  const getReturnDate = (index: number) =>
    params.returnDates?.[index] ?? params.returnDates?.[0];

  // Airports
  url = url.replaceAll("${ORIGIN_AIRPORT}", params.originAirport);
  url = url.replaceAll("${DESTINATION_AIRPORT}", params.destinationAirport);

  // Departure dates (1–4)
  for (let i = 1; i <= 4; i++) {
    const date = getDepartureDate(i - 1);
    if (date) {
      url = url.replaceAll(
        `\${DEPARTURE_DATE_${i}}`,
        formatMultiTypeDate(date, params.dateFormat),
      );
    }
  }

  // Return dates (optional)
  for (let i = 1; i <= 4; i++) {
    const date = getReturnDate(i - 1);
    if (date) {
      url = url.replaceAll(
        `\${RETURN_DATE_${i}}`,
        formatMultiTypeDate(date, params.dateFormat),
      );
    }
  }

  // Pax
  url = url.replaceAll("${NUMBER_OF_ADULTS}", String(params.adults));
  url = url.replaceAll("${NUMBER_OF_CHILDREN}", String(params.children));
  url = url.replaceAll("${NUMBER_OF_BABIES}", String(params.babies));

  return url;
};

/**
 * Executa a lógica de redirecionamento alternativo.
 */

export const handleAltRedirect = (
  originAirport: string,
  destinationAirport: string,
  altRedirectCampaign: AltRedirectCampaigns | null,
  isMobile: boolean,
  searchState: {
    departureDates: string[];
    returnDates?: string[] | null;
    adults: number;
    children: number;
    babies: number;
  },
): { execute: boolean; url?: string; name?: string } => {
  try {
    const currentCampaign = getAltRedirectCampaign(
      originAirport,
      destinationAirport,
      isMobile,
      altRedirectCampaign,
    );

    if (!currentCampaign) return { execute: false };

    if (getCookie(currentCampaign.COOKIE_NAME!) !== null) {
      return { execute: false };
    }

    let finalUrl = currentCampaign.URL;

    if (finalUrl.includes("${")) {
      finalUrl = resolveAltRedirectUrl(finalUrl, {
        originAirport,
        destinationAirport,
        departureDates: searchState.departureDates,
        returnDates: searchState.returnDates ?? undefined,
        adults: searchState.adults,
        children: searchState.children,
        babies: searchState.babies,
        dateFormat: currentCampaign.DATA_TYPE ?? "dd/mm/aaaa",
      });
    }

    if (searchState.children === 0) {
      finalUrl = finalUrl.replace(
        /&p\[1\]\.t=CHD&p\[1\]\.c=0&p\[1\]\.cp=false/g,
        "",
      );
    }

    if (searchState.babies === 0) {
      finalUrl = finalUrl.replace(
        /&p\[2\]\.t=INF&p\[2\]\.c=0&p\[2\]\.cp=false/g,
        "",
      );
    }

    if (!finalUrl.startsWith("http")) return { execute: false };

    setCookie(currentCampaign.COOKIE_NAME!, "true", 7);

    return {
      execute: true,
      url: finalUrl,
      name: currentCampaign.NAME,
    };
  } catch (error) {
    captureSentryException(error, {
      tags: {
        feature: "alt-redirect",
        operation: "handleAltRedirect",
      },
    });
    console.error("Erro em handleAltRedirect", error);
    return { execute: false };
  }
};
